import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactCountryFlag from "react-country-flag";
import Select, { components as selectComponents } from "react-select";
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumber,
} from "react-phone-number-input";
import {
  LuChevronRight,
  LuEye,
  LuEyeOff,
  LuLockKeyhole,
  LuMail,
  LuPhone,
} from "react-icons/lu";

import userController from "../../controllers/userController";
import { userService } from "../../services/userService";
import Utils from "../../utils/Utils";
import AccountActionButton from "./AccountActionButton";
import { translateLabel, translateText, useTranslationSnapshot } from "./../../translations/runtime";


const accountAccessItems = ["Email Address", "Phone Number", "Password"];

const otpDigits = ["", "", "", "", "", ""];

const initialPasswordForm = {
  currentPassword: "",
  newPassword: "",
  confirmPassword: "",
};

const passwordRules = [
  "Must have at least 8 characters",
  "Uppercase characters (A-Z)",
  "Lowercase characters (a-z)",
  "Digits (0-9)",
  "Special characters",
];

const countryNameFormatter =
  typeof Intl !== "undefined" && Intl.DisplayNames
    ? new Intl.DisplayNames(["en"], {
        type: "region",
      })
    : null;

const phoneCountryOptions = getCountries().map((countryCode) => ({
  value: countryCode,
  label: `+${getCountryCallingCode(countryCode)}`,
  countryName: countryNameFormatter?.of(countryCode) || countryCode,
}));

const matchesPhoneCountryOption = (option, inputValue) => {
  const searchValue = String(inputValue || "")
    .trim()
    .toLowerCase();

  if (!searchValue) {
    return true;
  }

  const searchDigits = searchValue.replace(/\D/g, "");
  const dialCode = String(option?.label || "");

  return (
    String(option?.countryName || "")
      .toLowerCase()
      .includes(searchValue) ||
    String(option?.value || "")
      .toLowerCase()
      .includes(searchValue) ||
    dialCode.toLowerCase().includes(searchValue) ||
    (searchDigits && dialCode.replace(/\D/g, "").includes(searchDigits))
  );
};

const filterPhoneCountryOption = ({ data }, inputValue) =>
  matchesPhoneCountryOption(data, inputValue);

const getResponseData = (response) => {
  return (
    response?.data?.data ??
    response?.data?.result ??
    response?.data ??
    response ??
    {}
  );
};

const getJwtFromStoredUser = (storedUser) => {
  if (typeof storedUser === "string") {
    return storedUser;
  }

  return (
    storedUser?.jwt ||
    storedUser?.token ||
    storedUser?.accessToken ||
    storedUser?.access_token ||
    ""
  );
};

const getUserIdFromPayload = (payload = {}) =>
  String(
    payload?.id ||
      payload?.userId ||
      payload?.userID ||
      payload?.UserId ||
      payload?.UserID ||
      payload?.sub ||
      payload?.user?.id ||
      payload?.user?.userId ||
      payload?.user?.userID ||
      "",
  ).trim();

const resolveAuthenticatedSession = () => {
  const storedUser = userService.getJwt();

  const jwt = String(getJwtFromStoredUser(storedUser) || "").trim();

  const payload =
    (jwt ? Utils.parseJwt(jwt) : null) || userService.getUserPayload() || {};

  const userID = getUserIdFromPayload(payload);

  return {
    jwt,
    userID,
  };
};

function CurrentPhoneNumber({ phone, isLoading, error }) {
  useTranslationSnapshot();
  if (isLoading) return <p>{translateLabel("txt_end_user_loading", "Loading...")}</p>;
  if (error) return <p className="vendor-account-password-error">{translateText(error)}</p>;
  if (!phone.number) return <p>-</p>;

  const number = phone.number.replace(/[^\d+]/g, "");
  const internationalNumber = number.startsWith("+")
    ? number
    : number.startsWith("00")
      ? `+${number.slice(2)}`
      : phone.suffix && number.startsWith(phone.suffix)
        ? `+${number}`
        : `+${phone.suffix}${number}`;
  const parsed = parsePhoneNumber(internationalNumber);
  const callingCode = parsed?.countryCallingCode || phone.suffix;
  const country =
    parsed?.country ||
    phoneCountryOptions.find(
      (option) => getCountryCallingCode(option.value) === callingCode,
    )?.value;

  return (
    <p dir="ltr">
      {country ? (
        <ReactCountryFlag
          className="vendor-account-phone-flag"
          countryCode={country}
          svg
        />
      ) : null}
      {callingCode ? <span>+{callingCode}</span> : null}
      <span>{parsed?.nationalNumber || phone.number}</span>
    </p>
  );
}

function CountryOption({ value, label, countryName }) {
  useTranslationSnapshot();
  return (
    <span className="vendor-account-phone-country-option">
      <ReactCountryFlag countryCode={value} svg />

      <span>{countryName}</span>
      <span>{translateText(label)}</span>
    </span>
  );
}

function PhoneCountryMenuList(props) {
  useTranslationSnapshot();
  const {
    children,
    selectProps: {
      countrySearchValue,
      countrySearchInputRef,
      onCountrySearchChange,
    },
  } = props;

  return (
    <selectComponents.MenuList {...props}>
      <div
        className="vendor-account-phone-country-search"
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <input
          ref={countrySearchInputRef}
          aria-label={translateLabel("input_web_vendor_search_phone_suffix", "Search phone suffix")}
          placeholder={translateLabel("input_web_vendor_search_suffix_placeholder", "Search suffix")}
          type="text"
          value={countrySearchValue}
          onChange={(event) => onCountrySearchChange(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
        />
      </div>

      {children}
    </selectComponents.MenuList>
  );
}

function OtpFields({ otpForm, setOtpForm }) {
  useTranslationSnapshot();
  const inputRefs = useRef([]);

  const handleChange = (index, event) => {
    const value = event.target.value.replace(/\D/g, "").slice(-1);

    const nextOtp = [...otpForm];

    nextOtp[index] = value;

    setOtpForm(nextOtp);

    if (value && index < otpForm.length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !otpForm[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();

    const pastedValue = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    if (!pastedValue) {
      return;
    }

    const nextOtp = ["", "", "", "", "", ""];

    pastedValue.split("").forEach((digit, index) => {
      nextOtp[index] = digit;
    });

    setOtpForm(nextOtp);

    const focusIndex = Math.min(pastedValue.length, 5);

    inputRefs.current[focusIndex]?.focus();
  };

  return (
    <div className="vendor-account-otp-grid">
      {otpForm.map((digit, index) => (
        <input
          key={index}
          ref={(element) => {
            inputRefs.current[index] = element;
          }}
          aria-label={translateLabel("txt_common_otp_digit_value1", "OTP digit {value1}", { value1: index + 1 })}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(event) => handleChange(index, event)}
          onKeyDown={(event) => handleKeyDown(index, event)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
}

function AccountAccessPanel({
  backSignal = 0,
  initialStep = "list",
  onGoHome,
  onStepChange,
}) {
  useTranslationSnapshot();
  const [step, setStep] = useState(initialStep);

  const [isPhoneWarningOpen, setIsPhoneWarningOpen] = useState(false);

  const [currentEmail, setCurrentEmail] = useState("");

  const [currentPhone, setCurrentPhone] = useState({ number: "", suffix: "" });

  const [emailForm, setEmailForm] = useState("");

  const [isUserLoading, setIsUserLoading] = useState(false);

  const [userLoadError, setUserLoadError] = useState("");

  const [emailError, setEmailError] = useState("");

  const [emailMessage, setEmailMessage] = useState("");

  const [isCheckingEmail, setIsCheckingEmail] = useState(false);

  const [isSendingOtp, setIsSendingOtp] = useState(false);

  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);

  const [otpError, setOtpError] = useState("");

  const [phoneCountry, setPhoneCountry] = useState("LB");

  const [phoneForm, setPhoneForm] = useState("+961");

  const [phoneCountrySearch, setPhoneCountrySearch] = useState("");

  const [isPhoneCountryMenuOpen, setIsPhoneCountryMenuOpen] = useState(false);

  const [phoneError, setPhoneError] = useState("");

  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  const [isUpdatingPhone, setIsUpdatingPhone] = useState(false);

  const [otpForm, setOtpForm] = useState(otpDigits);

  const [passwordForm, setPasswordForm] = useState(initialPasswordForm);

  const [passwordError, setPasswordError] = useState("");

  const [isCheckingPassword, setIsCheckingPassword] = useState(false);

  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const [passwordVisible, setPasswordVisible] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const hasMounted = useRef(false);
  const phoneCountrySelectRef = useRef(null);
  const phoneCountrySearchInputRef = useRef(null);
  const phoneCountryMenuOpeningRef = useRef(false);

  const focusPhoneCountrySearch = () => {
    phoneCountryMenuOpeningRef.current = true;

    window.setTimeout(() => {
      phoneCountrySearchInputRef.current?.focus();
      phoneCountryMenuOpeningRef.current = false;
    }, 0);
  };

  const closePhoneCountryMenu = () => {
    setIsPhoneCountryMenuOpen(false);
    setPhoneCountrySearch("");
  };

  const setAccessStep = (nextStep) => {
    setStep(nextStep);

    onStepChange?.(nextStep);
  };

  const loadCurrentUser = async () => {
    try {
      setIsUserLoading(true);
      setUserLoadError("");

      const { jwt, userID } = resolveAuthenticatedSession();

      if (!jwt || !userID) {
        setUserLoadError("Unable to load account information.");

        return;
      }

      const response = await userController.getUser({
        userID,
        jwt,
      });

      const user = getResponseData(response);

      const email = String(user?.email || "").trim();

      setCurrentEmail(email);
      setCurrentPhone({
        number: String(
          user?.phoneNumber ||
            user?.phone ||
            user?.phone_number ||
            user?.mobileNumber ||
            user?.mobile ||
            "",
        ).trim(),
        suffix: String(
          user?.suffix ??
            user?.phoneSuffix ??
            user?.countryCallingCode ??
            "961",
        ).replace(/\D/g, ""),
      });
    } catch (error) {
      console.error(
        "[ACCOUNT ACCESS] GET USER FAILED:",
        error?.response?.data || error,
      );

      setUserLoadError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to load account information.",
      );
    } finally {
      setIsUserLoading(false);
    }
  };

  useEffect(() => {
    loadCurrentUser();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;

      return;
    }

    if (isPhoneWarningOpen) {
      setIsPhoneWarningOpen(false);

      return;
    }

    if (step !== "list") {
      resetFlow();
      return;
    }

    onGoHome?.();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backSignal]);

  useEffect(() => {
    if (!isPhoneCountryMenuOpen) {
      return undefined;
    }

    const handleDocumentMouseDown = (event) => {
      const target = event.target;

      if (
        phoneCountrySelectRef.current?.contains(target) ||
        target?.closest?.(".vendor-account-phone-country__menu")
      ) {
        return;
      }

      closePhoneCountryMenu();
    };

    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isPhoneCountryMenuOpen]);

  const resetFlow = () => {
    setAccessStep("list");

    setEmailForm("");
    setEmailError("");
    setEmailMessage("");
    setOtpError("");

    setIsCheckingEmail(false);

    setIsSendingOtp(false);

    setIsVerifyingOtp(false);

    setPhoneCountry("LB");
    setPhoneForm("+961");
    setPhoneCountrySearch("");
    setIsPhoneCountryMenuOpen(false);
    setPhoneError("");
    setIsCheckingPhone(false);
    setIsUpdatingPhone(false);

    setOtpForm(["", "", "", "", "", ""]);

    setPasswordForm(initialPasswordForm);
    setPasswordError("");
    setIsCheckingPassword(false);
    setIsUpdatingPassword(false);

    setIsPhoneWarningOpen(false);
  };

  const handleEmailUpdate = async () => {
    const newEmail = String(emailForm || "")
      .trim()
      .toLowerCase();

    const oldEmail = String(currentEmail || "")
      .trim()
      .toLowerCase();

    setEmailError("");
    setEmailMessage("");
    setOtpError("");

    if (!newEmail) {
      setEmailError("Please enter your new email address.");

      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newEmail)) {
      setEmailError("Please enter a valid email address.");

      return;
    }

    if (!oldEmail) {
      setEmailError("Current email address is missing.");

      return;
    }

    if (newEmail === oldEmail) {
      setEmailError("Please enter a different email address.");

      return;
    }

    try {
      setIsCheckingEmail(true);

      const checkResponse = await userController.checkIfEmailInUse({
        email: newEmail,
      });

      const checkData = getResponseData(checkResponse);

      const emailExists = checkData === true || checkData === "true";

      if (emailExists) {
        setEmailError("This email address is already in use.");

        return;
      }

      setEmailMessage("Email is available. Sending OTP...");

      setIsCheckingEmail(false);

      setIsSendingOtp(true);

      const otpResponse = await userController.sendOtp({
        email: oldEmail,

        otpEmailSubject: "D2i2a w Noss Email Verification",
      });

      const otpData = getResponseData(otpResponse);

      const otpSent = otpData === true || otpData === "true";

      if (!otpSent) {
        setEmailMessage("");

        setEmailError("Unable to send OTP. Please try again.");

        return;
      }

      setEmailMessage("");

      setOtpForm(["", "", "", "", "", ""]);

      setAccessStep("email-otp");
    } catch (error) {
      console.error(
        "[EMAIL] CHECK/SEND OTP FAILED:",
        error?.response?.data || error,
      );

      setEmailMessage("");

      setEmailError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to continue. Please try again.",
      );
    } finally {
      setIsCheckingEmail(false);

      setIsSendingOtp(false);
    }
  };

  const handleResendEmailOtp = async () => {
    const oldEmail = String(currentEmail || "")
      .trim()
      .toLowerCase();

    setOtpError("");

    if (!oldEmail) {
      setOtpError("Current email address is missing.");

      return;
    }

    try {
      setIsSendingOtp(true);

      const response = await userController.sendOtp({
        email: oldEmail,

        otpEmailSubject: "D2i2a w Noss Email Verification",
      });

      const data = getResponseData(response);

      const sent = data === true || data === "true";

      if (!sent) {
        setOtpError("Unable to resend OTP.");

        return;
      }

      setOtpForm(["", "", "", "", "", ""]);
    } catch (error) {
      console.error(
        "[EMAIL] RESEND OTP FAILED:",
        error?.response?.data || error,
      );

      setOtpError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to resend OTP.",
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyEmailOtp = async () => {
    const otp = otpForm.join("").trim();

    const oldEmail = String(currentEmail || "")
      .trim()
      .toLowerCase();

    const newEmail = String(emailForm || "")
      .trim()
      .toLowerCase();

    setOtpError("");

    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setOtpError("Please enter the 6-digit OTP code.");

      return;
    }

    if (!oldEmail) {
      setOtpError("Current email address is missing.");

      return;
    }

    if (!newEmail) {
      setOtpError("New email address is missing.");

      return;
    }

    try {
      setIsVerifyingOtp(true);

      const verifyOtpDTO = {
        email: oldEmail,
        otp: Number(otp),
      };

      const verifyResponse = await userController.verifyEmailOtp({
        verifyOtpDTO,
      });

      const verifyData = getResponseData(verifyResponse);

      const verifyMessage = String(verifyData?.message || "").toLowerCase();

      const verified = verifyMessage.includes("otp verified");

      if (!verified) {
        setOtpError(verifyData?.message || "Invalid OTP code.");

        return;
      }

      const { jwt, userID } = resolveAuthenticatedSession();

      if (!jwt || !userID) {
        setOtpError("Unable to identify current user.");

        return;
      }

      const changeResponse = await userController.changeEmail({
        newEmail,
        userID,
        jwt,
      });

      const changeData = getResponseData(changeResponse);

      const changed = changeData === true || changeData === "true";

      if (!changed) {
        setOtpError("OTP verified, but the email could not be changed.");

        return;
      }

      setCurrentEmail(newEmail);

      setAccessStep("email-success");
    } catch (error) {
      console.error(
        "[EMAIL] VERIFY/CHANGE FAILED:",
        error?.response?.data || error,
      );

      setOtpError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to verify OTP or change email.",
      );
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const phoneDialCode = `+${getCountryCallingCode(phoneCountry)}`;

  const filteredPhoneCountryOptions = useMemo(
    () =>
      phoneCountryOptions.filter((option) =>
        matchesPhoneCountryOption(option, phoneCountrySearch),
      ),
    [phoneCountrySearch],
  );

  const getPhoneParts = () => {
    const suffix = String(getCountryCallingCode(phoneCountry)).replace(
      /\D/g,
      "",
    );

    const newPhoneNumber = String(phoneForm || "")
      .replace(phoneDialCode, "")
      .replace(/\D/g, "")
      .trim();

    return {
      suffix,
      newPhoneNumber,
      fullPhoneNumber: `+${suffix}${newPhoneNumber}`,
    };
  };

  const handlePhoneUpdate = async () => {
    const { suffix, newPhoneNumber, fullPhoneNumber } = getPhoneParts();

    setPhoneError("");
    setOtpError("");

    if (!newPhoneNumber) {
      setPhoneError("Please enter your new phone number.");
      return;
    }

    try {
      setIsCheckingPhone(true);

      const response = await userController.checkExistsPhoneNumber({
        phoneNumber: newPhoneNumber,
        suffix: suffix,
      });

      const data = response.data;
      const phoneExists = data === true || data === "true";

      if (phoneExists) {
        setPhoneError("This phone number is already in use.");
        return;
      }

      setIsPhoneWarningOpen(true);
    } catch (error) {
      console.error("[PHONE] CHECK FAILED:", error?.response?.data || error);

      setPhoneError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to check phone number.",
      );
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const handleContinuePhoneUpdate = async () => {
    const email = String(currentEmail || "")
      .trim()
      .toLowerCase();

    setPhoneError("");
    setOtpError("");

    if (!email) {
      setIsPhoneWarningOpen(false);

      setPhoneError("Current email address is missing.");

      return;
    }

    try {
      setIsSendingOtp(true);

      const response = await userController.sendOtp({
        email,
        otpEmailSubject: "D2i2a w Noss Phone Number Verification",
      });

      const data = getResponseData(response);

      const sent = data === true || data === "true";

      if (!sent) {
        setPhoneError("Unable to send OTP. Please try again.");

        return;
      }

      setOtpForm(["", "", "", "", "", ""]);

      setIsPhoneWarningOpen(false);

      setAccessStep("phone-otp");
    } catch (error) {
      console.error("[PHONE] SEND OTP FAILED:", error?.response?.data || error);

      setPhoneError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to send OTP.",
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleResendPhoneOtp = async () => {
    const email = String(currentEmail || "")
      .trim()
      .toLowerCase();

    setOtpError("");

    if (!email) {
      setOtpError("Current email address is missing.");

      return;
    }

    try {
      setIsSendingOtp(true);

      const response = await userController.sendOtp({
        email,
        otpEmailSubject: "D2i2a w Noss Phone Number Verification",
      });

      const data = getResponseData(response);

      const sent = data === true || data === "true";

      if (!sent) {
        setOtpError("Unable to resend OTP.");

        return;
      }

      setOtpForm(["", "", "", "", "", ""]);
    } catch (error) {
      console.error(
        "[PHONE] RESEND OTP FAILED:",
        error?.response?.data || error,
      );

      setOtpError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to resend OTP.",
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyPhoneOtp = async () => {
    const otp = otpForm.join("").trim();

    const email = String(currentEmail || "")
      .trim()
      .toLowerCase();

    const { suffix, newPhoneNumber } = getPhoneParts();

    setOtpError("");

    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setOtpError("Please enter the 6-digit OTP code.");

      return;
    }

    if (!email) {
      setOtpError("Current email address is missing.");

      return;
    }

    if (!newPhoneNumber) {
      setOtpError("New phone number is missing.");

      return;
    }

    try {
      setIsVerifyingOtp(true);

      const verifyResponse = await userController.verifyEmailOtp({
        verifyOtpDTO: {
          email,
          otp: Number(otp),
        },
      });

      const verifyData = getResponseData(verifyResponse);

      const verifyMessage = String(verifyData?.message || "").toLowerCase();

      const verified =
        verifyData === true ||
        verifyData === "true" ||
        verifyMessage.includes("otp verified");

      if (!verified) {
        setOtpError(verifyData?.message || "Invalid OTP code.");

        return;
      }

      const { jwt, userID } = resolveAuthenticatedSession();

      if (!jwt || !userID) {
        setOtpError("Unable to identify current user.");

        return;
      }

      setIsUpdatingPhone(true);

      const updateResponse = await userController.updateUserPhoneNumber({
        newPhoneNumber,
        suffix,
        userId: userID,
        Authorization: userService.getJwt(),
      });

      const updateData = getResponseData(updateResponse);

      const updated =
        updateData === true ||
        updateData === "true" ||
        updateResponse?.status === 200 ||
        updateResponse?.status === 201 ||
        updateResponse?.status === 204;

      if (!updated) {
        setOtpError("OTP verified, but the phone number could not be changed.");

        return;
      }

      setCurrentPhone({ number: newPhoneNumber, suffix });
      setAccessStep("phone-success");
    } catch (error) {
      console.error(
        "[PHONE] VERIFY/UPDATE FAILED:",
        error?.response?.data || error,
      );

      setOtpError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to verify OTP or change phone number.",
      );
    } finally {
      setIsVerifyingOtp(false);

      setIsUpdatingPhone(false);
    }
  };

  const validateNewPassword = (password) => {
    if (password.length < 8) {
      return "Password must have at least 8 characters.";
    }

    if (!/[A-Z]/.test(password)) {
      return "Password must contain an uppercase character.";
    }

    if (!/[a-z]/.test(password)) {
      return "Password must contain a lowercase character.";
    }

    if (!/\d/.test(password)) {
      return "Password must contain a digit.";
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      return "Password must contain a special character.";
    }

    return "";
  };

  const handlePasswordUpdate = async () => {
    const currentPassword = String(passwordForm.currentPassword || "");
    const newPassword = String(passwordForm.newPassword || "");
    const confirmPassword = String(passwordForm.confirmPassword || "");
    const email = String(currentEmail || "")
      .trim()
      .toLowerCase();

    setPasswordError("");
    setOtpError("");

    if (!currentPassword) {
      setPasswordError("Please enter your current password.");
      return;
    }

    if (!newPassword) {
      setPasswordError("Please enter your new password.");
      return;
    }

    if (!confirmPassword) {
      setPasswordError("Please confirm your new password.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match. Please try again.");
      setAccessStep("password-error");
      return;
    }

    const passwordValidationError = validateNewPassword(newPassword);

    if (passwordValidationError) {
      setPasswordError(passwordValidationError);
      return;
    }

    if (!email) {
      setPasswordError("Current email address is missing.");
      return;
    }

    try {
      setIsCheckingPassword(true);

      const checkPassword = {
        email,
        password: currentPassword,
        phone: "",
      };

      const checkResponse = await userController.checkPassword({
        checkPassword,
      });

      const checkData = getResponseData(checkResponse);

      const validCurrentPassword = checkData === true || checkData === "true";

      if (!validCurrentPassword) {
        setPasswordError("Current password is incorrect.");
        return;
      }

      setIsCheckingPassword(false);
      setIsSendingOtp(true);

      const otpResponse = await userController.sendOtp({
        email,
        otpEmailSubject: "D2i2a w Noss Email Verification",
      });

      const otpData = getResponseData(otpResponse);

      const otpSent = otpData === true || otpData === "true";

      if (!otpSent) {
        setPasswordError(
          "Current password is correct, but OTP could not be sent.",
        );
        return;
      }

      setOtpForm(["", "", "", "", "", ""]);

      setAccessStep("password-otp");
    } catch (error) {
      console.error(
        "[PASSWORD] CHECK/SEND OTP FAILED:",
        error?.response?.data || error,
      );

      setPasswordError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to update password. Please try again.",
      );
    } finally {
      setIsCheckingPassword(false);
      setIsSendingOtp(false);
    }
  };

  const handleResendPasswordOtp = async () => {
    const email = String(currentEmail || "")
      .trim()
      .toLowerCase();

    setOtpError("");

    if (!email) {
      setOtpError("Current email address is missing.");
      return;
    }

    try {
      setIsSendingOtp(true);

      const response = await userController.sendOtp({
        email,
        otpEmailSubject: "D2i2a w Noss Email Verification",
      });

      const data = getResponseData(response);

      const sent = data === true || data === "true";

      if (!sent) {
        setOtpError("Unable to resend OTP.");
        return;
      }

      setOtpForm(["", "", "", "", "", ""]);
    } catch (error) {
      console.error(
        "[PASSWORD] RESEND OTP FAILED:",
        error?.response?.data || error,
      );

      setOtpError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to resend OTP.",
      );
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyPasswordOtp = async () => {
    const otp = otpForm.join("").trim();
    const email = String(currentEmail || "")
      .trim()
      .toLowerCase();
    const newPassword = String(passwordForm.newPassword || "");
    const confirmPassword = String(passwordForm.confirmPassword || "");

    setOtpError("");

    if (otp.length !== 6 || !/^\d{6}$/.test(otp)) {
      setOtpError("Please enter the 6-digit OTP code.");
      return;
    }

    if (!email) {
      setOtpError("Current email address is missing.");
      return;
    }

    if (!newPassword || !confirmPassword) {
      setOtpError("New password information is missing.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setOtpError("Passwords do not match.");
      return;
    }

    try {
      setIsVerifyingOtp(true);

      const verifyResponse = await userController.verifyEmailOtp({
        verifyOtpDTO: {
          email,
          otp: Number(otp),
        },
      });

      const verifyData = getResponseData(verifyResponse);

      const verifyMessage = String(verifyData?.message || "").toLowerCase();

      const verified =
        verifyData === true ||
        verifyData === "true" ||
        verifyMessage.includes("otp verified");

      if (!verified) {
        setOtpError(verifyData?.message || "Invalid OTP code.");
        return;
      }

      setIsUpdatingPassword(true);

      const setPasswordDTO = {
        confirmPassword,
        emailOrPhone: email,
        newPassword,
        otp,
      };

      const setPasswordResponse = await userController.setPassword({
        setPasswordDTO,
      });

      const setPasswordData = getResponseData(setPasswordResponse);

      const passwordUpdated =
        setPasswordResponse?.status === 200 ||
        setPasswordResponse?.status === 201 ||
        setPasswordData === true ||
        setPasswordData === "true" ||
        (setPasswordData && typeof setPasswordData === "object");

      if (!passwordUpdated) {
        setOtpError("Unable to change password.");
        return;
      }

      setAccessStep("password-success");
    } catch (error) {
      console.error(
        "[PASSWORD] VERIFY/SET PASSWORD FAILED:",
        error?.response?.data || error,
      );

      setOtpError(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to verify OTP or change password.",
      );
    } finally {
      setIsVerifyingOtp(false);
      setIsUpdatingPassword(false);
    }
  };

  const completeFlow = () => {
    resetFlow();
    onGoHome?.();
  };

  return (
    <>
      {step === "list" ? (
        <div className="vendor-account-access-menu">
          {accountAccessItems.map((title) => (
            <button
              className="vendor-account-access-tile"
              key={title}
              onClick={
                title === "Email Address"
                  ? () => setAccessStep("email-current")
                  : title === "Phone Number"
                    ? () => setAccessStep("phone-current")
                    : () => setAccessStep("password-edit")
              }
              type="button"
            >
              <span>{translateText(title)}</span>

              <LuChevronRight className="navigation-direction-icon" aria-hidden="true" />
            </button>
          ))}
        </div>
      ) : null}

      {step === "email-current" ? (
        <div className="vendor-account-email-flow">
          <div className="vendor-account-email-path">
            {translateLabel("txt_user_profile", "User Profile")} / {translateLabel("lnk_account_access", "Account Access")} /<span> {translateLabel("txt_change_email_address", "Change Email Address")}</span>
          </div>

          <p className="vendor-account-email-copy"> {translateLabel("txt_email_updates_description", "Your email is useful not only for logging in, but to receive important updates too!")} </p>

          {userLoadError ? (
            <p className="vendor-account-password-error" role="alert">{translateText(userLoadError)}</p>
          ) : null}

          <div className="vendor-account-email-current">
            <div className="vendor-account-email-label">
              <LuMail aria-hidden="true" />

              <span>{translateLabel("txt_current_email", "Current Email")}</span>
            </div>

            <p>{isUserLoading ? translateLabel("txt_end_user_loading", "Loading...") : currentEmail || "-"}</p>
          </div>

          <button
            className="vendor-account-email-link"
            onClick={() => {
              setEmailForm("");

              setEmailError("");

              setEmailMessage("");

              setAccessStep("email-edit");
            }}
            type="button"
          > {translateLabel("txt_change_email_address", "Change Email Address")} </button>

          <div className="vendor-account-email-actions">
            <AccountActionButton
              onClick={() => {
                setEmailForm("");

                setEmailError("");

                setEmailMessage("");

                setAccessStep("email-edit");
              }}
            > {translateLabel("btn_update", "Update")} </AccountActionButton>

            <AccountActionButton color="dark" onClick={resetFlow}> {translateLabel("btn_cancel", "Cancel")} </AccountActionButton>
          </div>
        </div>
      ) : null}

      {step === "email-edit" ? (
        <div className="vendor-account-email-flow">
          <div className="vendor-account-email-path">
            <span>{translateLabel("txt_change_email_address", "Change Email Address")}</span>
          </div>

          <p className="vendor-account-email-copy"> {translateLabel("txt_email_updates_description", "Your email is useful not only for logging in, but to receive important updates too!")} </p>

          <div className="vendor-account-email-current">
            <div className="vendor-account-email-label">
              <LuMail aria-hidden="true" />

              <span>{translateLabel("txt_current_email", "Current Email")}</span>
            </div>

            <p>{currentEmail || "-"}</p>
          </div>

          <label className="vendor-account-email-field">
            <span className="vendor-account-email-label">
              <LuMail aria-hidden="true" />

              <span>{translateLabel("input_new_email", "New Email")}</span>
            </span>

            <input
              placeholder="xxxx@xxx.com"
              type="email"
              value={emailForm}
              disabled={isCheckingEmail || isSendingOtp}
              onChange={(event) => {
                setEmailForm(event.target.value);

                setEmailError("");

                setEmailMessage("");
              }}
            />
          </label>

          {emailError ? (
            <p className="vendor-account-password-error" role="alert">{translateText(emailError)}</p>
          ) : null}

          {emailMessage ? <p>{translateText(emailMessage)}</p> : null}

          <div className="vendor-account-email-actions">
            <AccountActionButton
              onClick={handleEmailUpdate}
              disabled={isCheckingEmail || isSendingOtp}
            >
              {isSendingOtp
                ? translateLabel("txt_web_vendor_sending_otp", "Sending OTP...")
                : isCheckingEmail
                  ? translateLabel("txt_end_user_checking", "Checking...")
                  : translateLabel("btn_update", "Update")}
            </AccountActionButton>

            <AccountActionButton
              color="dark"
              onClick={resetFlow}
              disabled={isCheckingEmail || isSendingOtp}
            > {translateLabel("btn_cancel", "Cancel")} </AccountActionButton>
          </div>
        </div>
      ) : null}

      {step === "email-otp" ||
      step === "phone-otp" ||
      step === "password-otp" ? (
        <div className="vendor-account-email-flow">
          {step === "phone-otp" || step === "password-otp" ? (
            <div className="vendor-account-email-path">
              <span>{step === "phone-otp" ? translateLabel("input_phone_number", "Phone Number") : translateLabel("input_password", "Password")}</span>
            </div>
          ) : null}

          <div className="vendor-account-email-path vendor-account-validation-title">
            <span>{translateLabel("txt_end_user_validation", "Validation")}</span>
          </div>

          <p className="vendor-account-email-copy">
            {translateLabel("txt_web_vendor_enter_sent_code_prefix", "Enter the code we sent to")}{" "}<strong>{currentEmail}</strong>
          </p>

          <div className="vendor-account-otp-block">
            <strong>{translateLabel("input_otp_code", "Enter OTP code")}</strong>

            <OtpFields otpForm={otpForm} setOtpForm={setOtpForm} />

            {otpError ? (
              <p className="vendor-account-password-error" role="alert">{translateText(otpError)}</p>
            ) : null}
          </div>

          <div className="vendor-account-resend">
            <strong>{translateLabel("txt_code_not_received", "Didn't receive the code?")}</strong>

            <button
              type="button"
              disabled={isSendingOtp}
              onClick={
                step === "email-otp"
                  ? handleResendEmailOtp
                  : step === "phone-otp"
                    ? handleResendPhoneOtp
                    : handleResendPasswordOtp
              }
            >
              <LuMail aria-hidden="true" />

              <span>{isSendingOtp ? translateLabel("txt_end_user_sending", "Sending...") : translateLabel("txt_end_user_resend_code", "Resend code")}</span>
            </button>
          </div>

          <div className="vendor-account-email-actions vendor-account-email-actions-single">
            <AccountActionButton
              onClick={
                step === "email-otp"
                  ? handleVerifyEmailOtp
                  : step === "phone-otp"
                    ? handleVerifyPhoneOtp
                    : handleVerifyPasswordOtp
              }
              disabled={isVerifyingOtp || isUpdatingPhone || isUpdatingPassword}
            >
              {isUpdatingPhone || isUpdatingPassword
                ? translateLabel("txt_end_user_updating", "Updating...")
                : isVerifyingOtp
                  ? translateLabel("txt_end_user_verifying", "Verifying...")
                  : translateLabel("txt_end_user_verify", "Verify")}
            </AccountActionButton>
          </div>
        </div>
      ) : null}

      {step === "email-success" || step === "phone-success" ? (
        <div className="vendor-account-email-flow vendor-account-email-success">
          <div className="vendor-account-email-path">
            <span>
              {step === "email-success" ? translateLabel("txt_end_user_validation", "Validation") : translateLabel("input_phone_number", "Phone Number")}
            </span>
          </div>

          <p>
            {step === "email-success"
              ? translateLabel("txt_end_user_your_email_address_successfully_changed", "Your email address successfully changed!")
              : translateLabel("txt_end_user_your_phone_number_successfully_changed", "Your phone number successfully changed!")}
          </p>

          <div className="vendor-account-email-actions vendor-account-email-actions-single">
            <AccountActionButton width="150px" onClick={completeFlow}> {translateLabel("txt_end_user_go_to_homepage", "Go to Homepage")} </AccountActionButton>
          </div>
        </div>
      ) : null}

      {step === "phone-current" ? (
        <div className="vendor-account-email-flow">
          <div className="vendor-account-email-path">
            {translateLabel("txt_user_profile", "User Profile")} / {translateLabel("lnk_account_access", "Account Access")} /<span> {translateLabel("input_phone_number", "Phone Number")}</span>
          </div>

          <p className="vendor-account-email-copy">{translateLabel("txt_web_vendor_change_phone_description", "Change your phone number")}</p>

          <div className="vendor-account-email-current">
            <div className="vendor-account-email-label">
              <LuPhone aria-hidden="true" />

              <span>{translateLabel("txt_web_vendor_current_phone_number", "Current Phone Number")}</span>
            </div>

            <CurrentPhoneNumber
              phone={currentPhone}
              isLoading={isUserLoading}
              error={userLoadError}
            />
          </div>

          <button
            className="vendor-account-email-link"
            onClick={() => setAccessStep("phone-edit")}
            type="button"
          >
            {translateLabel("btn_web_vendor_change_phone_number", "Change Phone Number")}
          </button>
        </div>
      ) : null}

      {step === "phone-edit" ? (
        <div className="vendor-account-email-flow">
          <div className="vendor-account-email-path">
            <span>{translateLabel("input_phone_number", "Phone Number")}</span>
          </div>

          <p className="vendor-account-email-copy">{translateLabel("txt_web_vendor_change_phone_description", "Change your phone number")}</p>

          <div className="vendor-account-email-current">
            <div className="vendor-account-email-label">
              <LuPhone aria-hidden="true" />

              <span>{translateLabel("txt_web_vendor_current_phone_number", "Current Phone Number")}</span>
            </div>

            <CurrentPhoneNumber
              phone={currentPhone}
              isLoading={isUserLoading}
              error={userLoadError}
            />
          </div>

          <label className="vendor-account-email-field">
            <span className="vendor-account-email-label">
              <LuPhone aria-hidden="true" />

              <span> {translateLabel("txt_web_vendor_new_phone_number", "New Phone Number")} <i>*</i>
              </span>
            </span>

            <div
              className="vendor-account-phone-input"
              ref={phoneCountrySelectRef}
            >
              <Select
                className="vendor-account-phone-country-select"
                classNamePrefix="vendor-account-phone-country"
                components={{ MenuList: PhoneCountryMenuList }}
                countrySearchInputRef={phoneCountrySearchInputRef}
                countrySearchValue={phoneCountrySearch}
                filterOption={filterPhoneCountryOption}
                formatOptionLabel={(option) => <CountryOption {...option} />}
                isSearchable={false}
                menuIsOpen={isPhoneCountryMenuOpen}
                menuPlacement="bottom"
                menuPosition="fixed"
                noOptionsMessage={() => translateLabel("txt_web_vendor_no_matching_suffix", "No matching suffix")}
                onCountrySearchChange={setPhoneCountrySearch}
                onChange={(option) => {
                  const nextCountry = option?.value || "LB";

                  setPhoneCountry(nextCountry);

                  setPhoneForm(`+${getCountryCallingCode(nextCountry)}`);

                  closePhoneCountryMenu();
                  setPhoneError("");
                }}
                onMenuClose={() => {
                  if (phoneCountryMenuOpeningRef.current) {
                    return;
                  }

                  closePhoneCountryMenu();
                }}
                onMenuOpen={() => {
                  setIsPhoneCountryMenuOpen(true);
                  focusPhoneCountrySearch();
                }}
                options={filteredPhoneCountryOptions}
                value={
                  phoneCountryOptions.find(
                    (option) => option.value === phoneCountry,
                  ) || phoneCountryOptions[0]
                }
              />

              <input
                inputMode="numeric"
                pattern="[0-9]*"
                type="tel"
                value={phoneForm.replace(phoneDialCode, "")}
                disabled={isCheckingPhone}
                onChange={(event) => {
                  setPhoneForm(
                    `${phoneDialCode}${event.target.value.replace(/\D/g, "")}`,
                  );

                  setPhoneError("");
                }}
              />
            </div>
          </label>

          {phoneError ? (
            <p className="vendor-account-password-error" role="alert">{translateText(phoneError)}</p>
          ) : null}

          <div className="vendor-account-email-actions">
            <AccountActionButton
              onClick={handlePhoneUpdate}
              disabled={isCheckingPhone}
            >
              {isCheckingPhone ? translateLabel("txt_end_user_checking", "Checking...") : translateLabel("btn_update", "Update")}
            </AccountActionButton>

            <AccountActionButton
              color="dark"
              onClick={resetFlow}
              disabled={isCheckingPhone}
            > {translateLabel("btn_cancel", "Cancel")} </AccountActionButton>
          </div>
        </div>
      ) : null}

      {step === "password-edit" || step === "password-error" ? (
        <div className="vendor-account-email-flow">
          <div className="vendor-account-email-path">
            <span>{translateLabel("input_password", "Password")}</span>
          </div>

          <p className="vendor-account-email-copy">{translateLabel("txt_web_vendor_change_password_description", "Change your password.")}</p>

          {[
            ["currentPassword", translateLabel("input_current_password", "Current Password")],
            ["newPassword", translateLabel("input_new_password", "New Password")],
            ["confirmPassword", translateLabel("txt_web_vendor_confirm_password", "Confirm Password")],
          ].map(([field, label]) => (
            <label
              className={`vendor-account-password-field${
                field === "confirmPassword" &&
                step === "password-error" &&
                passwordForm.newPassword !== passwordForm.confirmPassword
                  ? " vendor-account-password-field-error"
                  : ""
              }`}
              key={field}
            >
              <span className="vendor-account-email-label">
                <LuLockKeyhole aria-hidden="true" />

                <span>{translateText(label)}</span>
              </span>

              <span className="vendor-account-password-input">
                <input
                  type={passwordVisible[field] ? "text" : "password"}
                  value={passwordForm[field]}
                  onChange={(event) => {
                    setPasswordForm((current) => ({
                      ...current,
                      [field]: event.target.value,
                    }));
                    setPasswordError("");
                    if (step === "password-error") {
                      setAccessStep("password-edit");
                    }
                  }}
                />

                <button
                  aria-label={translateLabel("txt_web_vendor_toggle_value1", "Toggle {value1}", { value1: label })}
                  onClick={() =>
                    setPasswordVisible((current) => ({
                      ...current,
                      [field]: !current[field],
                    }))
                  }
                  type="button"
                >
                  {passwordVisible[field] ? (
                    <LuEye aria-hidden="true" />
                  ) : (
                    <LuEyeOff aria-hidden="true" />
                  )}
                </button>
              </span>
            </label>
          ))}

          {step === "password-edit" ? (
            <ul className="vendor-account-password-rules">
              {passwordRules.map((rule) => (
                <li key={rule}>{translateText(rule)}</li>
              ))}
            </ul>
          ) : null}

          {passwordError ? (
            <p className="vendor-account-password-error" role="alert">{translateText(passwordError)}</p>
          ) : null}

          <div className="vendor-account-email-actions">
            <AccountActionButton
              onClick={handlePasswordUpdate}
              disabled={isCheckingPassword || isSendingOtp}
            >
              {isSendingOtp
                ? translateLabel("txt_web_vendor_sending_otp", "Sending OTP...")
                : isCheckingPassword
                  ? translateLabel("txt_end_user_checking", "Checking...")
                  : translateLabel("btn_update", "Update")}
            </AccountActionButton>

            <AccountActionButton color="dark" onClick={resetFlow}> {translateLabel("btn_cancel", "Cancel")} </AccountActionButton>
          </div>
        </div>
      ) : null}

      {step === "password-success" ? (
        <div className="vendor-account-email-flow vendor-account-email-success">
          <div className="vendor-account-email-path">
            <span>{translateLabel("input_password", "Password")}</span>
          </div>

          <p>{translateLabel("txt_web_vendor_your_password_successfully_changed", "Your password successfully changed!")}</p>

          <div className="vendor-account-email-actions vendor-account-email-actions-single">
            <AccountActionButton width="150px" onClick={completeFlow}> {translateLabel("txt_end_user_go_to_homepage", "Go to Homepage")} </AccountActionButton>
          </div>
        </div>
      ) : null}

      {isPhoneWarningOpen ? (
        <div className="vendor-account-warning-backdrop">
          <div
            className="vendor-account-warning-modal"
            role="dialog"
            aria-modal="true"
          >
            <button
              aria-label={translateLabel("txt_web_vendor_close_warning", "Close warning")}
              className="vendor-account-warning-close"
              onClick={() => setIsPhoneWarningOpen(false)}
              type="button"
            >
              x
            </button>

            <div className="vendor-account-warning-icon">!</div>

            <h2>{translateLabel("txt_warning", "Warning")}</h2>

            <p> {translateLabel("txt_web_vendor_if_you_continue_you_will_not_be_able_to_change_your_phone_number_again_before_60_d", "If you continue you will not be able to change your phone number again before 60 days from now.")} </p>

            <div className="vendor-account-warning-actions">
              <AccountActionButton
                color="danger"
                width="72px"
                onClick={handleContinuePhoneUpdate}
                disabled={isSendingOtp}
              >
                {isSendingOtp ? translateLabel("txt_end_user_sending", "Sending...") : translateLabel("btn_continue", "Continue")}
              </AccountActionButton>

              <AccountActionButton
                color="dark"
                width="72px"
                onClick={() => setIsPhoneWarningOpen(false)}
                disabled={isSendingOtp}
              > {translateLabel("txt_end_user_later", "Later")} </AccountActionButton>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default AccountAccessPanel;
